/**
 * A pipeline a document walks along, clickable in both directions.
 *
 * A labelled current stage plus a connected track of the rest, so it reads as
 * "where this thing is" rather than a row of independent toggles. Advancing
 * *and* regressing, because the reducers allow any transition and for the
 * reason they do: a brewer who tapped "packaged" a day early has to be able to
 * go back, and pretending otherwise only teaches people to lie to the app.
 * A filing stage — `archived` — is never on the track: it is not another thing
 * that happens to the beer, so it is reached from the Archive / Unarchive item
 * in a list's row menu (`components/batches/batch-list.tsx` and
 * `components/blending/blending-list.tsx`) and, when the document is in it,
 * nothing here is lit.
 *
 * Generic over the stage union because the batch's phases and the blend's are
 * the same control drawn twice (D-BS5). `stages` is the linear progression
 * only, `labels` names every member of the union so the current stage can be
 * named even when it is off the track, and the two caption strings are the
 * caller's because "Phase" and "Brewing phase" are things a batch says. A
 * generic control that defaulted to a brewing word would be this component
 * knowing about one of its two callers.
 */

import { CheckIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StageStepperProps<S extends string> {
  /** The linear progression, in order. Stages off the track are left out. */
  readonly stages: readonly S[];
  /** A name for every stage, including the ones `stages` omits. */
  readonly labels: Readonly<Record<S, string>>;
  /** Where the document is now, on the track or off it. */
  readonly current: S;
  readonly onChange: (stage: S) => void;
  /** The small caption above the current stage's name, e.g. "Phase". */
  readonly caption: string;
  /** The track's accessible name, e.g. "Brewing phase". */
  readonly trackLabel: string;
  /**
   * A sentence under the current stage's name, for a stage that needs one.
   *
   * Off the track is the case it exists for: a document nothing here is lit for
   * has both the room and the need, and the slot beside the empty counter is
   * where a reader is already looking for what the stage means. The caller's
   * words, like `caption` and `trackLabel`, so this stays a control that knows
   * nothing about beer.
   */
  readonly note?: string | undefined;
  // Spelled with the `undefined` an `exactOptionalPropertyTypes` project needs
  // to let a wrapper forward its own optional `className` straight through.
  readonly className?: string | undefined;
}

export function StageStepper<S extends string>({
  stages,
  labels,
  current,
  onChange,
  caption,
  trackLabel,
  note,
  className,
}: StageStepperProps<S>) {
  const currentIndex = stages.indexOf(current);
  const lastIndex = stages.length - 1;
  const alongPipeline = currentIndex >= 0;

  return (
    <div className={cn('rounded-lg border border-border bg-card px-3 py-3 sm:px-4', className)}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {caption}
          </p>
          <p className="text-base font-semibold tracking-tight">{labels[current]}</p>
          {note !== undefined && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
        </div>
        {alongPipeline && (
          <p className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
            {currentIndex + 1} of {stages.length}
          </p>
        )}
      </div>
      <ol className="m-0 flex w-full list-none p-0" aria-label={trackLabel}>
        {stages.map((step, index) => {
          const isCurrent = step === current;
          // Everything before the current stage is behind us. When the document
          // is off the track, `currentIndex` is −1 and nothing is lit.
          const isPast = alongPipeline && index < currentIndex;
          const isLast = index === lastIndex;
          // Incoming segment is complete once this stage has been reached;
          // outgoing only after it has been left.
          const incomingFilled = alongPipeline && index > 0 && index <= currentIndex;
          const outgoingFilled = isPast;
          return (
            <li key={step} className="min-w-0 flex-1">
              <button
                type="button"
                aria-current={isCurrent ? 'step' : undefined}
                onClick={() => onChange(step)}
                className={cn(
                  'flex w-full flex-col items-center gap-1.5 rounded-md px-0.5',
                  'min-h-11 focus-visible:ring-[2px] focus-visible:ring-ring/40 focus-visible:outline-none',
                )}
              >
                <span className="flex h-6 w-full items-center">
                  <span
                    aria-hidden
                    className={cn(
                      'h-0.5 flex-1',
                      index === 0 && 'opacity-0',
                      incomingFilled ? 'bg-primary' : 'bg-border',
                    )}
                  />
                  <span
                    aria-hidden
                    className={cn(
                      'relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full border-2 bg-card',
                      isPast && 'border-primary bg-primary text-primary-foreground',
                      isCurrent &&
                        'border-primary bg-primary text-primary-foreground ring-4 ring-primary/25',
                      !isPast && !isCurrent && 'border-border',
                    )}
                  >
                    {isPast ? (
                      <CheckIcon className="size-3.5" />
                    ) : isCurrent ? (
                      <span className="size-2 rounded-full bg-primary-foreground" />
                    ) : null}
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'h-0.5 flex-1',
                      isLast && 'opacity-0',
                      outgoingFilled ? 'bg-primary' : 'bg-border',
                    )}
                  />
                </span>
                <span
                  className={cn(
                    'text-center text-[11px] leading-tight font-medium',
                    isCurrent && 'text-foreground',
                    isPast && 'text-foreground',
                    !isPast && !isCurrent && 'text-muted-foreground',
                  )}
                >
                  {labels[step]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
